import { authRequest, AuthApiError } from '@/lib/api/auth-client';
import { apiGet, apiPost, requireApiData } from '@/lib/api/client';

export { AuthApiError as SelfOnboardApiError };

export type ValidateSelfOnboardResponse = {
  valid: boolean;
  orgName?: string;
  orgLogoUrl?: string | null;
  maskedEmail?: string;
  lockedFields?: {
    workEmail: string;
    orgRoleName: string;
    departmentName: string;
    designation: string;
  };
  editableHints?: { firstName: string; lastName: string };
  expiresAt?: string;
};

export type SelfOnboardProfileDraft = {
  firstName: string;
  lastName: string;
  middleName?: string | null;
  dateOfBirth: string;
  gender?: string;
  personalPhone?: string;
  personalEmail?: string;
};

export async function validateSelfOnboardToken(token: string) {
  const envelope = await authRequest<ValidateSelfOnboardResponse>('/auth/self-onboard/validate', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  return envelope.data ?? { valid: false };
}

export async function startSelfOnboard(token: string, profile: SelfOnboardProfileDraft) {
  const envelope = await authRequest<{
    sessionId: string;
    otpSent: boolean;
    otpExpiresInSeconds: number;
    maskedEmail: string;
  }>('/auth/self-onboard/start', {
    method: 'POST',
    body: JSON.stringify({ token, profile }),
  });
  return requireApiData(envelope.data, 'Failed to start registration');
}

export async function verifySelfOnboardOtp(sessionId: string, otp: string) {
  const envelope = await authRequest<{
    setupToken: string;
    setupExpiresInSeconds: number;
  }>('/auth/self-onboard/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ sessionId, otp }),
  });
  return requireApiData(envelope.data, 'OTP verification failed');
}

export async function resendSelfOnboardOtp(sessionId: string) {
  const envelope = await authRequest<{ sent: boolean; otpExpiresInSeconds: number }>(
    '/auth/self-onboard/resend-otp',
    {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    }
  );
  return envelope.data ?? { sent: true, otpExpiresInSeconds: 120 };
}

export async function completeSelfOnboard(input: {
  sessionId: string;
  setupToken: string;
  password: string;
  confirmPassword: string;
}) {
  const envelope = await authRequest<{
    userId: string;
    employeeId: string;
    employeeCode: string;
    loginHint: { orgCode: string; workEmail: string };
  }>('/auth/self-onboard/complete', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return requireApiData(envelope.data, 'Registration failed');
}

export type SelfOnboardInviteRow = {
  inviteId: string;
  workEmail: string;
  orgRoleName: string;
  departmentName: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  completedAt: string | null;
};

export async function listSelfOnboardInvites(params?: {
  status?: string;
  email?: string;
  page?: number;
  limit?: number;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.email) query.set('email', params.email);
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const envelope = await apiGet<{
    items: SelfOnboardInviteRow[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }>(`/self-onboard/invites${suffix}`);
  return requireApiData(envelope.data, 'Failed to load invites');
}

export async function createSelfOnboardInvite(body: {
  workEmail: string;
  firstNameHint?: string;
  lastNameHint?: string;
  orgRoleId: string;
  departmentId: string;
  designation: string;
  reportingManagerId?: string | null;
  locationId?: string | null;
  expiresInHours?: number;
  sendEmail?: boolean;
  personalMessage?: string;
}) {
  const envelope = await apiPost<{
    inviteId: string;
    status: string;
    workEmail: string;
    orgRoleName: string;
    expiresAt: string;
    inviteLink: string;
  }>('/self-onboard/invites', body);
  return requireApiData(envelope.data, 'Failed to create invite');
}

export async function revokeSelfOnboardInvite(inviteId: string) {
  await apiPost<{ status: string }>(`/self-onboard/invites/${encodeURIComponent(inviteId)}/revoke`, {});
}

export async function resendSelfOnboardInviteEmail(inviteId: string) {
  await apiPost<{ sent: boolean }>(
    `/self-onboard/invites/${encodeURIComponent(inviteId)}/resend`,
    {}
  );
}

export async function getSelfOnboardInviteLink(inviteId: string) {
  const envelope = await apiGet<{ inviteLink: string }>(
    `/self-onboard/invites/${encodeURIComponent(inviteId)}/link`
  );
  return requireApiData(envelope.data, 'Failed to get invite link');
}
